import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, User, Calendar, Briefcase, CheckCircle, Clock, Edit } from "react-feather";
import SectionCard from "../../../../components/common/SectionCard";
import CompactInfoBlock, { CompactInfoField } from "../../../../components/common/CompactInfoBlock";
import CommissionModal from "../components/CommissionModal";
import { useListOrdersQuery } from "../../salesApi";
import { useCanManageCommission } from "../../useSalesPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { MAROON, MAROON_TINT } from "../../../../components/common/brandColors";
import { paths } from "../../../../routes/paths";

const money = (n) => Number(n || 0).toFixed(2);
const d = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "-");
// Thousands separators on the headline figure only. The order value is the number this
// screen is scanned for, and 1064010.00 is not readable at a glance without them.
const bigMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const daysSince = (v) => {
  if (!v) return null;
  const then = new Date(v);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
};

// How long an unpaid commission has been sitting. Colour carries it so a card that needs
// chasing is picked out of a wall of cards without reading a single date - once the
// commission IS paid the age stops mattering, so a paid card goes deliberately neutral
// rather than staying red for the rest of its life.
const AGE_BANDS = [
  { maxDays: 7, color: "#0f7b3f", tint: "#e8f5ee", label: (n) => (n <= 0 ? "closed today" : `${n}d ago`) },
  { maxDays: 30, color: "#b26a00", tint: "#fdf3e3", label: (n) => `${n}d ago` },
  { maxDays: Infinity, color: "#b3261e", tint: "#fdecea", label: (n) => `${n}d ago · chase` },
];
const NEUTRAL_BAND = { color: "#5a6472", tint: "#f4f6f8", label: (n) => (n == null ? "" : `${n}d ago`) };

const ageBand = (days, isPaid) => {
  if (isPaid || days == null) return NEUTRAL_BAND;
  return AGE_BANDS.find((b) => days <= b.maxDays);
};

const PAID_FILTERS = [
  { value: "", label: "Paid & Not Paid" },
  { value: "UNPAID", label: "Not Paid" },
  { value: "PAID", label: "Paid" },
];

// Commission worklist.
//
// Commission is only ever recorded against a CLOSED order, so this screen is
// pinned to `view=CLOSED` - the server-side view value that already existed,
// no new status was invented. Paid vs Not Paid is likewise not a status
// column: App_SalesOrderCommission either has a row for the order or it does
// not, and the API derives isCommissionPaid from that. So the paid filter is
// applied client-side over the closed set rather than through the existing
// COMMISSION_NOT_PAID server view - that one deliberately does NOT restrict
// to closed orders (its WHERE clause is just "no commission row"), so using
// it here would pull in open orders that are not eligible yet, and it has no
// "paid" counterpart to pair with.
const CommissionsPage = () => {
  const canManage = useCanManageCommission();
  const { selectedBranchId } = useBranchFilter();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paidFilter, setPaidFilter] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError, error } = useListOrdersQuery({
    branchId: selectedBranchId ?? undefined,
    view: "CLOSED",
    pageSize: 200,
  });

  const allClosed = useMemo(() => data?.items || [], [data]);

  // Built from the loaded rows rather than a separate users fetch: the
  // salespeople who actually have closed orders are the only ones this
  // screen can ever show, and it needs no extra permission.
  const salespeople = useMemo(() => {
    const names = new Set();
    allClosed.forEach((o) => { if (o.salespersonName) names.add(o.salespersonName); });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allClosed]);

  const items = allClosed.filter((o) => {
    if (salesperson && o.salespersonName !== salesperson) return false;
    if (paidFilter === "PAID" && !o.isCommissionPaid) return false;
    if (paidFilter === "UNPAID" && o.isCommissionPaid) return false;
    if (!search) return true;
    return (
      o.orderNumber?.toLowerCase().includes(search) ||
      o.customerName?.toLowerCase().includes(search) ||
      o.salespersonName?.toLowerCase().includes(search) ||
      o.closedReason?.toLowerCase().includes(search)
    );
  });

  const paidCount = items.filter((o) => o.isCommissionPaid).length;
  const totalPaid = items.reduce((sum, o) => sum + Number(o.commissionAmount || 0), 0);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Sales Commission</h4>
            <h6 className="text-muted">Closed orders only &mdash; a commission is paid once the order is complete</h6>
          </div>
        </div>

        <SectionCard icon={DollarSign} title="Filters">
          <div className="row g-2">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search order no, customer, salesperson or invoice"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select className="form-control form-control-sm" value={salesperson} onChange={(e) => setSalesperson(e.target.value)}>
                <option value="">All Salespeople</option>
                {salespeople.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <select className="form-control form-control-sm" value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
                {PAID_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <CompactInfoBlock className="mt-3">
            <CompactInfoField label="Closed orders" value={items.length} />
            <CompactInfoField label="Commission paid" value={`${paidCount} of ${items.length}`} />
            <CompactInfoField label="Total commission" value={money(totalPaid)} />
          </CompactInfoBlock>
        </SectionCard>

        {isLoading && <p>Loading...</p>}
        {isError && (
          <div className="alert alert-danger">Could not load closed orders ({error?.message || "server error"}).</div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <SectionCard icon={Briefcase} title="Nothing to show">
            <p className="text-muted mb-0">No closed order matches these filters.</p>
          </SectionCard>
        )}

        <div className="row">
          {items.map((o) => (
            <div className="col-xl-4 col-md-6" key={o.recordGuid}>
              <SectionCard
                icon={o.isCommissionPaid ? CheckCircle : Clock}
                title={
                  <Link to={paths.salesOrderDetail(o.recordGuid)} style={{ color: MAROON }}>{o.orderNumber}</Link>
                }
                right={
                  <span className={`badge ${o.isCommissionPaid ? "badge-linesuccess" : "badge-linewarning"}`}>
                    {o.isCommissionPaid ? "Paid" : "Not Paid"}
                  </span>
                }
              >
                <div className="fw-semibold mb-1" style={{ fontSize: 14 }}>{o.customerName}</div>
                <CompactInfoBlock className="mb-2">
                  <CompactInfoField label="Salesperson" value={o.salespersonName} icon={User} />
                  <CompactInfoField label="Branch" value={o.branchCode} />
                  <CompactInfoField label="Invoice" value={o.closedReason} />
                </CompactInfoBlock>

                {/* Closed date and order value are the two figures this screen is read
                    for - when the order became eligible, and how much it was worth - so
                    they come out of the run-on line of small print into their own boxes.
                    Colour on the date is the age of an UNPAID commission, so the cards
                    that need chasing are found by scanning rather than by reading. */}
                <KeyFigures order={o} />

                {o.isCommissionPaid ? (
                  <div
                    className="rounded p-2 mb-2"
                    style={{ background: MAROON_TINT, border: `1px solid ${MAROON}33` }}
                  >
                    <div style={{ color: MAROON, fontWeight: 800, fontSize: 16 }}>{money(o.commissionAmount)}</div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      Paid {d(o.commissionPaidDate)}
                      {o.commissionChequeNumber ? ` · ${o.commissionChequeNumber}` : ""}
                    </div>
                    {o.commissionRemarks && (
                      <div className="text-muted fst-italic" style={{ fontSize: 11 }}>{o.commissionRemarks}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-muted mb-2" style={{ fontSize: 12.5 }}>No commission recorded yet.</div>
                )}

                {canManage && (
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${o.isCommissionPaid ? "btn-outline-secondary" : "btn-primary"}`}
                    onClick={() => setEditing(o)}
                  >
                    <Edit size={14} className="me-1" />
                    {o.isCommissionPaid ? "Edit / Reverse" : "Record Commission"}
                  </button>
                )}
              </SectionCard>
            </div>
          ))}
        </div>
      </div>

      <CommissionModal show={Boolean(editing)} order={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

// The two boxed figures on every commission card. Equal halves rather than a headline and
// a footnote: neither number is subordinate to the other - the date says whether to act,
// the value says how much it is worth.
const KeyFigures = ({ order }) => {
  const days = daysSince(order.closedDate);
  const band = ageBand(days, order.isCommissionPaid);

  return (
    <div className="row g-2 mb-2">
      <div className="col-6">
        <div
          className="rounded p-2 h-100"
          style={{ background: band.tint, border: `1px solid ${band.color}33` }}
        >
          <div className="text-uppercase" style={{ fontSize: 10, letterSpacing: 0.5, color: band.color, opacity: 0.85 }}>
            Closed
          </div>
          <div className="d-flex align-items-center gap-1" style={{ color: band.color, fontWeight: 700, fontSize: 14 }}>
            <Calendar size={13} /> {d(order.closedDate)}
          </div>
          {days != null && (
            <div style={{ fontSize: 11, color: band.color, opacity: 0.9 }}>{band.label(days)}</div>
          )}
        </div>
      </div>
      <div className="col-6">
        <div className="rounded p-2 h-100" style={{ background: MAROON_TINT, border: `1px solid ${MAROON}33` }}>
          <div className="text-uppercase" style={{ fontSize: 10, letterSpacing: 0.5, color: MAROON, opacity: 0.85 }}>
            Order value
          </div>
          <div style={{ color: MAROON, fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
            {bigMoney(order.totalAmount)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionsPage;
